import { useEffect, useState } from 'react';
import PortalSidebar from '../components/PortalSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const iconFor = (message) => {
  const m = message.toLowerCase();
  if (m.includes('document') || m.includes('upload')) return { icon: 'request_quote', bg: 'bg-[#8fb3ff]/20', color: 'text-[#365da3]' };
  if (m.includes('payment') || m.includes('fee')) return { icon: 'payments', bg: 'bg-[#e0e3e5]', color: 'text-[#44474f]' };
  if (m.includes('message') || m.includes('agent')) return { icon: 'mail', bg: 'bg-[#ffdad8]/60', color: 'text-[#862026]' };
  return { icon: 'update', bg: 'bg-[#00255a]/5', color: 'text-[#00255a]' };
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return new Date(dateStr).toLocaleDateString(undefined, { weekday: 'long' });
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const groupNotifications = (list) => {
  const now = Date.now();
  const groups = { today: [], week: [], earlier: [] };
  list.forEach((n) => {
    const ageHours = (now - new Date(n.createdAt).getTime()) / 3600000;
    if (ageHours < 24) groups.today.push(n);
    else if (ageHours < 24 * 7) groups.week.push(n);
    else groups.earlier.push(n);
  });
  return groups;
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  const fetchNotifications = () => {
    fetch(`${API_BASE}/portal/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setNotifications(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchNotifications, [token]);

  const markRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    await fetch(`${API_BASE}/portal/notifications/${id}/read`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    await fetch(`${API_BASE}/portal/notifications/read-all`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading notifications...</p>;

  const { today, week, earlier } = groupNotifications(notifications);
  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <PortalSidebar />
      <main className="md:ml-64 pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-[#00255a]">Notifications</h2>
              <p className="text-[#64748b] mt-1">Stay updated on your application journey.</p>
            </div>
            <button
              onClick={markAllRead}
              disabled={!hasUnread}
              className="flex items-center gap-2 px-4 py-2 border border-[#00255a]/20 text-[#00255a] rounded-lg font-semibold text-sm hover:bg-[#00255a]/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <span className="material-symbols-outlined text-[18px]">done_all</span>
              Mark all as read
            </button>
          </div>

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="material-symbols-outlined text-[64px] text-[#e0e3e5] mb-4">notifications_off</span>
              <h3 className="text-xl font-bold text-[#00255a]">All caught up!</h3>
              <p className="text-[#64748b] mt-2 max-w-sm">
                No notifications at this time. We'll let you know when there's an update on your journey.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {today.length > 0 && (
                <NotificationGroup title="Today" items={today} onRead={markRead} />
              )}
              {week.length > 0 && (
                <NotificationGroup title="This Week" items={week} onRead={markRead} />
              )}
              {earlier.length > 0 && (
                <section>
                  <h3 className="text-xs text-[#64748b] uppercase tracking-widest mb-6 font-semibold">Earlier</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {earlier.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => markRead(n.id)}
                        className="group flex flex-col justify-between bg-white/40 p-6 rounded-2xl border border-[#e2e8f0] transition-all cursor-pointer hover:bg-white"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-[#e0e3e5]/40 flex items-center justify-center text-[#44474f]">
                            <span className="material-symbols-outlined">{iconFor(n.message).icon}</span>
                          </div>
                          <span className="text-[12px] text-[#64748b]">{timeAgo(n.createdAt)}</span>
                        </div>
                        <p className="text-[#44474f] line-clamp-2">{n.message}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function NotificationGroup({ title, items, onRead }) {
  return (
    <section>
      <h3 className="text-xs text-[#64748b] uppercase tracking-widest mb-6 font-semibold">{title}</h3>
      <div className="flex flex-col gap-4">
        {items.map((n) => {
          const { icon, bg, color } = iconFor(n.message);
          return (
            <div
              key={n.id}
              onClick={() => onRead(n.id)}
              className={`group relative flex items-start gap-4 p-5 rounded-2xl border border-[#e2e8f0] transition-all cursor-pointer ${
                n.isRead ? 'bg-white/60' : 'bg-white shadow-[0px_10px_30px_rgba(30,60,114,0.05)] hover:shadow-[0px_20px_40px_rgba(30,60,114,0.12)]'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center flex-shrink-0 ${color}`}>
                <span className="material-symbols-outlined">{icon}</span>
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <h4 className="font-semibold text-[#00255a]">Update</h4>
                  <span className="text-[12px] text-[#64748b]">{timeAgo(n.createdAt)}</span>
                </div>
                <p className="text-[#44474f]">{n.message}</p>
              </div>
              {!n.isRead && <div className="absolute top-5 right-2 w-2 h-2 rounded-full bg-[#ff7b7b]" />}
            </div>
          );
        })}
      </div>
    </section>
  );
}